import { Tabs } from "@repo/ui/Navigation/tabs";
import type { FC } from "react";
import {
  matchPath,
  resolvePath,
  useLocation,
  useResolvedPath,
} from "react-router";

export interface RouteTabItem {
  label: string;
  /** Route target, relative to the current route (like a `NavLink`'s `to`). */
  to: string;
  /**
   * Keep the tab selected on descendant routes too (like `NavLink`'s
   * `end={false}`). Defaults to exact matching.
   */
  matchDescendants?: boolean;
  isDisabled?: boolean;
}

export interface RouteTabsProps {
  tabs: readonly RouteTabItem[];
  ariaLabel?: string;
}

/**
 * The page header's tab strip for router-driven tabs: resolves each tab's
 * relative `to` against the current route and selects the deepest tab whose
 * path matches the location (via `matchPath`, so matching stays
 * case-insensitive like the router's own), so pages only declare their tab
 * targets.
 */
export const RouteTabs: FC<RouteTabsProps> = ({ tabs, ariaLabel }) => {
  const { pathname } = useLocation();
  const basePath = useResolvedPath(".").pathname;

  const resolvedTabs = tabs.map((tab) => ({
    ...tab,
    path: resolvePath(tab.to, basePath).pathname,
  }));

  const selectedTab = resolvedTabs
    .filter(
      (tab) =>
        matchPath(
          { path: tab.path, end: !(tab.matchDescendants ?? false) },
          pathname,
        ) !== null,
    )
    .sort((a, b) => b.path.length - a.path.length)[0];

  const items = resolvedTabs.map((tab) => ({
    id: tab.path,
    label: tab.label,
    href: tab.path,
    isDisabled: tab.isDisabled,
  }));

  return (
    <Tabs
      tabs={items}
      selectedTabId={selectedTab?.path}
      ariaLabel={ariaLabel}
    />
  );
};
